import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { FocusEvent } from 'react';
import { useCallback } from 'react';

import { useValidateOnBlur } from 'shared/lib/useValidateOnBlur';

import type { TPassportFormFields } from 'entities/creditForm';

const LINKED_PASSPORT_VALIDATION_FIELDS: Array<keyof TPassportFormFields> = [
  'birthDate',
  'passportIssueDate',
  'passportNumber',
];

/**
 * Валидация связанных полей паспортного блока на blur.
 *
 * Зачем:
 * - Поля `passportNumber`, `passportIssueDate`, `birthDate` связаны общей проверкой "Паспорт недействителен".
 * - По UX не нужно запускать связанную проверку, пока пользователь не ввел и не покинул `birthDate`.
 * - До этого момента не подсвечиваем `birthDate` и `passportIssueDate` при blur других полей.
 *
 * Какие кейсы закрывает:
 * 1) Пользователь вышел из поля `passportIssueDate`, но поле `birthDate` еще не трогал:
 *    - НЕ валидируем `passportIssueDate`;
 *    - триггерим только `passportNumber`.
 *
 * 2) Пользователь вышел из поля `passportNumber`, но поле `birthDate` еще не трогал:
 *    - валидируем текущее поле `passportNumber`;
 *    - из связанных полей исключаем `birthDate` и `passportIssueDate`, чтобы не получить преждевременные ошибки.
 *
 * 3) Пользователь вышел из поля `birthDate`:
 *    - валидируем текущее поле `birthDate`;
 *    - триггерим связанную валидацию `passportIssueDate` и `passportNumber`.
 *
 * 4) После того как `birthDate` была touched:
 *    - `passportIssueDate` снова участвует в обычной связанной валидации при blur связанных полей.
 */
export const useValidatePassportLinkedFieldsOnBlur = (fieldName: string, isValidateEmptyValue = true) => {
  const { trigger, getFieldState } = useFormContext<TPassportFormFields>();
  const validateFieldOnBlur = useValidateOnBlur(fieldName, isValidateEmptyValue);

  return useCallback(
    (event?: FocusEvent<HTMLElement>) => {
      const isBirthDateTouched = getFieldState('birthDate').isTouched;
      const shouldValidateBirthDate = isBirthDateTouched || fieldName === 'birthDate';
      const shouldValidatePassportIssueDate = isBirthDateTouched || fieldName === 'birthDate';

      if (fieldName !== 'passportIssueDate' || shouldValidatePassportIssueDate) {
        validateFieldOnBlur(event);
      }

      const dependentFieldNames = LINKED_PASSPORT_VALIDATION_FIELDS.filter((name) => {
        if (name === fieldName) {
          return false;
        }

        if (name === 'passportIssueDate' && !shouldValidatePassportIssueDate) {
          return false;
        }

        return !(name === 'birthDate' && !shouldValidateBirthDate);
      });
      trigger(dependentFieldNames);
    },
    [fieldName, getFieldState, trigger, validateFieldOnBlur],
  );
};
